import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useLocation } from 'react-router-dom';
import GamePrev from '../Assets/gameComponents/GamePrev';
import AuthProtected from '../HOCs/AuthProtected';
import { formatDatetimeString } from '../../utils/general';
import { nflTeams } from '../../utils/nflUtils';
import { nbaTeams } from '../../utils/nbaUtils';
import { mlbTeams } from '../../utils/mlbUtils';
import UpcomingGame from './UpcomingGame';
// API imports
import { useGetInSeasonSportsQuery } from '../../services/api/appApi';
import { useGetMlbUpcomingGamesQuery } from '../../services/api/mlbApi';
import { useGetNflUpcomingGamesQuery } from '../../services/api/nflApi';
import { useGetNbaUpcomingGamesQuery } from '../../services/api/nbaApi';
import { useGetUserSystemsQuery, useGetUpcomingMatchesMutation } from '../../services/api/systemsApi';
// images 
import BaseballIcon from '@assets/images/icons/sports/baseball.svg?react'
import FootballIcon from '@assets/images/icons/sports/football.svg?react'
import BasketballIcon from '@assets/images/icons/sports/basketball.svg?react'

const UpcomingGames = () => {
    // Hooks
    const location = useLocation();

    // State
    const [sport, setSport] = useState(location.state?.sport || "mlb");
    const [selectedGame, setSelectedGame] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showList, setShowList] = useState(true);
    const [getUpcomingMatches, { data: systemMatches, isLoading: isLoadingMatches }] = useGetUpcomingMatchesMutation();

    // Query inSeason sports
    const { data: inSeasonSports } = useGetInSeasonSportsQuery();

    // Conditionally fetch upcoming games based on in-season status
    const { data: mlbUpcoming } = useGetMlbUpcomingGamesQuery(undefined, {
        skip: !inSeasonSports?.mlb?.inSeason,
    });

    const { data: nflUpcoming } = useGetNflUpcomingGamesQuery(undefined, {
        skip: !inSeasonSports?.nfl?.inSeason,
    });

    const { data: nbaUpcoming } = useGetNbaUpcomingGamesQuery(undefined, {
        skip: !inSeasonSports?.nba?.inSeason,
    });

    // Set default sport to first inSeason sport (with games), if none in location state
    useEffect(() => {
        if(!location.state?.sport && inSeasonSports){
            // Find the first sport that is in season, with games   
            for(const sport in inSeasonSports){
                if(inSeasonSports[sport].inSeason){
                    if(sport === "mlb" && mlbUpcoming?.length > 0){
                        setSport(sport);
                        break;
                    }else if(sport === "nfl" && nflUpcoming?.length > 0){
                        setSport(sport);
                        break;
                    }else if(sport === "nba" && nbaUpcoming?.length > 0){
                        setSport(sport);
                        break;
                    }
                }
            }
        }else{
            setSport(location.state?.sport || "mlb");
        }
    }, [location.state?.sport, inSeasonSports, mlbUpcoming, nflUpcoming, nbaUpcoming]);

    // Then use the appropriate data based on selected sport
    const sportUpcoming = useMemo(() => {
        switch(sport) {
            case "mlb": return mlbUpcoming || [];
            case "nfl": 
                return nflUpcoming?.filter(game => game.status === "upcoming") || [];
            case "nba": return nbaUpcoming || [];
            default: return [];
        }
    }, [sport, mlbUpcoming, nflUpcoming, nbaUpcoming]);

    

    // Get user systems and upcoming matches
    const { data: userSystems } = useGetUserSystemsQuery();

    // Use useEffect to trigger the mutation when userSystems changes
    useEffect(() => {
        if (userSystems) {
            // Structure the data
            const ids = [
                ...(userSystems.private?.map(system => system._id.toString()) || []),
                ...(Object.values(userSystems?.followed || {}).flat().map(system => system._id.toString()) || [])
            ];
            // Only trigger if we have system IDs
            if (ids.length > 0) {
                getUpcomingMatches(ids).unwrap();
            }
        }
    }, [userSystems]);

    // Add resize listener
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Calculate match counts once when matches change
    const matchCounts = useMemo(() => {
        const counts = {};
        if (Array.isArray(systemMatches)) {
            for(const match of systemMatches){
                if(match["game_id"] in counts){
                    counts[match["game_id"]] += 1;
                } else {
                    counts[match["game_id"]] = 1;
                }
            }
        }
        return counts;
    }, [systemMatches]);

    // Calculate upcoming games based on sport and match counts
    const upcoming = useMemo(() => {
        let tmpUpcoming = [];
        if(sportUpcoming){
            tmpUpcoming = sportUpcoming.map(game => {
                const matchCount = matchCounts[game._id] || null;
                const dateObj = formatDatetimeString(game.date);
                let homeTeam, awayTeam;
                if(sport === "mlb"){
                    homeTeam = mlbTeams[game.home.team];
                    awayTeam = mlbTeams[game.away.team];
                }else if(sport === "nfl"){
                    homeTeam = nflTeams[game.home.team_id];
                    awayTeam = nflTeams[game.away.team_id];
                }else if(sport === "nba"){
                    homeTeam = nbaTeams[game.home.team_id];
                    awayTeam = nbaTeams[game.away.team_id];
                }
                return { ...game, homeTeam, awayTeam, dateObj, matchCount };
            });
        }
        return tmpUpcoming;
    }, [sport, sportUpcoming, matchCounts]);

    // Reset Selected Game when sport changes
    useEffect(() => {
        setSelectedGame(null);
    }, [sport]);

    // Select a game to display in detail panel
    const selectGame = useCallback((game) => {
        setSelectedGame(game);
        if (isMobile) {
            setShowList(false);
        }
    }, [isMobile]);

    // Go back to list view on mobile
    const backToList = useCallback(() => {
        setShowList(true);
    }, []);

    // Check location state for game selection when navigating back
    useEffect(() => {
        if (location.state?.game) {
            setSelectedGame(location.state.game);
            setSport(location.state.sport || 'mlb');
            setShowList(false);
        }
    }, [location.state, location.pathname]);

    return (
        <div className="bg-white dark:bg-brand-dark-900 h-full">
            <div className="flex flex-col md:flex-row h-full">
                {/* Left Column - Games List */}
                {(!isMobile || showList) && (
                    <aside className="md:w-72 md:border-r md:border-gray-200 dark:md:border-gray-700 bg-white dark:bg-brand-dark-900 flex flex-col h-full">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Upcoming Games</h2>
                            <div className="mt-4 flex gap-2">
                                <button 
                                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors flex-1 justify-center ${
                                        sport === "mlb" 
                                            ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                    }`}
                                    onClick={() => setSport("mlb")}>
                                    <BaseballIcon className="h-5 w-5 dark:fill-white" />
                                    <span>MLB</span>
                                </button>
                                <button 
                                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors flex-1 justify-center ${
                                        sport === "nfl" 
                                            ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                    }`}
                                    onClick={() => setSport("nfl")}>
                                    <FootballIcon className="h-5 w-5 dark:fill-white" />
                                    <span>NFL</span>
                                </button>
                                <button 
                                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors flex-1 justify-center ${
                                        sport === "nba" 
                                            ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                    }`}
                                    onClick={() => setSport("nba")}>
                                    <BasketballIcon className="h-5 w-5 dark:fill-white" />
                                    <span>NBA</span>
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            {upcoming && upcoming.length > 0 ? (
                                <div className="space-y-2">
                                    {upcoming.map((game) => (
                                        <GamePrev 
                                            key={game._id} 
                                            game={game} 
                                            sport={sport} 
                                            onClick={() => selectGame(game)}
                                            isActive={selectedGame && selectedGame._id === game._id}
                                        />                 
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <h6 className="text-gray-500 dark:text-gray-400">No Upcoming Games</h6>
                                </div>
                            )}
                        </div>
                    </aside>
                )}
                
                {/* Main Content - Game Details */}
                {(!isMobile || !showList) && (
                    <main className="flex-1 overflow-y-auto">
                        {/* Game Details Content */}
                        {(!isMobile || !showList) && selectedGame ? (
                            <div className="p-4">
                                <UpcomingGame 
                                    game={selectedGame} 
                                    sport={sport} 
                                    location={{ state: { game: selectedGame, sport } }}
                                    params={{ id: selectedGame._id }}
                                    isMobile={isMobile}
                                    backToList={backToList}
                                />
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full p-4">
                                <div className="text-center">
                                    <h3 className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">Select a game from the list</h3>
                                    <p className="mt-1 text-gray-500 dark:text-gray-400">Game details will appear here</p>
                                </div>
                            </div>
                        )}
                    </main>
                )}
            </div>
        </div>
    )
}

export default AuthProtected(UpcomingGames);